/**
 * The tour state machine + the single mount point for the overlay and the launcher.
 *
 * WHY IT LIVES AT App() AND NOT IN A HEADER: this app has no shared authenticated layout. Every
 * module renders its own <AppHeader> inside its own route element (TimekeepingModule.jsx:20,
 * FleetModule.jsx:28, …), so anything mounted in that chrome UNMOUNTS on every cross-module
 * navigation — and a tour that dies when you follow it to the next screen is not a tour. The only
 * slot that survives a route change is a sibling of <Routes> inside App(), which is exactly where
 * <AnnouncementModal /> and <OfflineBanner /> already sit.
 *
 * The admin-host branch of App() (App.jsx:44-67) deliberately does NOT mount this. That host is the
 * platform Super Admin console — a different audience with a different app, not new hires at a
 * client company.
 */
import { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { QueryClientContext } from '@tanstack/react-query'

import { useAuth } from '../../hooks/useAuth.jsx'
import { useTenantBranding } from '../../contexts/TenantContext'
import { useModuleSwitches } from '../../contexts/ModuleSwitchContext.jsx'
import { TourContext } from './tourContext'
import { TOURS } from './tours'
import { buildTourContext, availableTours } from './tourSelectors'
import { readProgress, writeProgress, clearProgress, resumeIndex } from './tourStorage'
import {
  armPractice, disarmPractice, practiceBlockedCount, purgePracticeQueries, subscribePracticeRuns,
} from './practiceMode'
import './practiceResponses'
import TourOverlay from './TourOverlay.jsx'

/**
 * How long a GATED step waits before it admits the person may be unable to do it and offers a way
 * past. Long enough that it is not nagging somebody who is reading, short enough that nobody sits in
 * front of a frozen-looking screen wondering whether the app has hung.
 */
const DEFAULT_STUCK_AFTER_MS = 15000

/**
 * @param {object} props
 * @param {number} [props.anchorTimeoutMs] how long to wait for a lazy chunk before calling an
 *        anchor missing. Overridable so tests do not have to burn four real seconds.
 * @param {boolean} [props.strict] force the dev "fail visibly" branch on or off. Defaults to the
 *        build mode via TourOverlay.
 * @param {Array} [props.tours] injectable registry — tests drive the engine with fixture tours
 *        rather than with whatever the app currently ships.
 */
export function TourProvider({
  children,
  anchorTimeoutMs,
  strict,
  tours = TOURS,
  stuckAfterMs = DEFAULT_STUCK_AFTER_MS,
}) {
  const { user, hasPermission } = useAuth()
  const branding = useTenantBranding()
  const { switches } = useModuleSwitches()
  const navigate = useNavigate()
  const location = useLocation()

  // `active` holds a SNAPSHOT of the filtered steps. Recomputing them mid-tour would let a
  // permissions refresh renumber the steps under a person who is halfway through.
  const [active, setActive] = useState(null)

  /**
   * The card shown AFTER a tour ends, and it is not decoration.
   *
   * Someone who believes they filed a real daily report and did not is a worse outcome than someone
   * who never took the tour at all. So a practice run does not simply vanish when it finishes or
   * when it is abandoned — it says, in words, that nothing was saved and that practice mode is now
   * off. `dismissed` gets the same card as `completed` for exactly that reason: walking out halfway
   * is the likeliest way to end up unsure what just happened.
   */
  const [finished, setFinished] = useState(null)

  const ctx = useMemo(
    () => buildTourContext({ user, features: branding && branding.features, hasPermission, switches }),
    // hasPermission is rebuilt on every AuthProvider render (it is a plain function in the context
    // value, not memoised there), so keying on it would rebuild this every render. `user` is what
    // actually determines its answers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, branding && branding.features, switches],
  )

  const entries = useMemo(() => availableTours(tours, ctx), [tours, ctx])

  const userId = user && user.id != null ? user.id : null

  /**
   * The one place a tour ends, so that finishing it, abandoning it, being logged out of it and
   * walking off the end of it cannot diverge — and, critically, so that the practice-run marker and
   * the "nothing was saved" card are impossible to end a tour WITHOUT.
   */
  const endTour = useCallback((run, reason) => {
    if (!run) return
    if (reason === 'completed') {
      writeProgress(userId, run.tour.id, { stepId: null, completedAt: Date.now() })
    }
    // The count from the run just ended. Only `armPractice` resets it, so reading it either side of
    // the disarm below gives the same answer.
    const blocked = practiceBlockedCount()
    if (run.tour.practice) {
      /**
       * DISARMED HERE, EXPLICITLY, AND BEFORE THE CARD GOES UP.
       *
       * The arming effect's cleanup also disarms — that is what covers the exits this function never
       * sees, being logged out and the app unmounting — and disarming twice is a no-op. But leaving
       * it to the cleanup alone put the "nothing was saved" card on screen one commit BEFORE the
       * screens behind it were told to drop what the run had fabricated, so for that commit the card
       * and the screen said opposite things about the same PIN. Doing it here means the disarm, the
       * screens' reset and the card are one React batch: the card never claims a state of the world
       * that has not happened yet.
       *
       * It is also what makes the ORDER a property of this function rather than of effect timing —
       * practice mode is off before any screen re-reads, or the re-read would be intercepted by the
       * mechanism it exists to undo.
       */
      disarmPractice()
    }
    setActive(null)
    if (run.tour.practice) {
      setFinished({
        tourId: run.tour.id,
        title: run.tour.title,
        reason,
        blocked,
      })
    }
  }, [userId])

  const stop = useCallback((reason) => endTour(active, reason), [active, endTour])

  const startTour = useCallback((tourId, { restart = false } = {}) => {
    const entry = entries.find((e) => e.tour.id === tourId)
    if (!entry) return
    if (restart) clearProgress(userId, tourId)
    const progress = restart ? null : readProgress(userId, tourId)
    let index = resumeIndex(entry.steps, progress)
    /**
     * A RESUMED RUN DOES NOT HAVE WHAT THE LAST ONE MADE. A practice run's record lives in the browser
     * for the run's length (practiceMode.registerPracticeRead), so after a reload or a break it starts
     * fresh — and a stored step that stands on something an earlier step made (the signature on the
     * PDF, the project on the award) would point at nothing, and be skipped in silence. Such a step
     * names the step that MAKES it (`resumeFrom`), and the run resumes there instead (review of
     * 17f785bc, F3). The card says why (`resumeNote`).
     */
    const resumed = !restart && index > 0
    if (resumed && entry.steps[index].resumeFrom) {
      const back = entry.steps.findIndex((s) => s.id === entry.steps[index].resumeFrom)
      if (back >= 0) index = back
    }
    setFinished(null)
    setActive({
      tour: entry.tour,
      steps: entry.steps,
      index,
      direction: 1,
      resumed,
    })
  }, [entries, userId])

  const goTo = useCallback((nextIndex, direction) => {
    if (!active) return
    if (nextIndex < 0) return                   // Back on step 1 is a no-op, not an exit.
    if (nextIndex >= active.steps.length) { endTour(active, 'completed'); return }
    writeProgress(userId, active.tour.id, { stepId: active.steps[nextIndex].id, completedAt: null })
    setActive({ ...active, index: nextIndex, direction, resumed: false })
  }, [active, userId, endTour])

  const next = useCallback(() => {
    if (!active) return
    const nextIndex = active.index + 1
    if (nextIndex >= active.steps.length) { endTour(active, 'completed'); return }
    writeProgress(userId, active.tour.id, { stepId: active.steps[nextIndex].id, completedAt: null })
    setActive({ ...active, index: nextIndex, direction: 1, resumed: false })
  }, [active, userId, endTour])

  // Back PERSISTS, exactly like next() and goTo(). It is not a "peek": someone who steps back to
  // re-read a step and is then called away — or just reloads, since `active` is React state and only
  // the stored stepId survives — must come back to the step they were STANDING on. Resuming at the
  // furthest step they ever reached would skip the one step they went back for, and nobody reports
  // that as a bug; they just never learn the thing.
  const back = useCallback(() => setActive((prev) => {
    if (!prev || prev.index <= 0) return prev
    const prevIndex = prev.index - 1
    writeProgress(userId, prev.tour.id, { stepId: prev.steps[prevIndex].id, completedAt: null })
    return { ...prev, index: prevIndex, direction: -1, resumed: false }
  }), [userId])

  /**
   * A step whose anchor never showed up. In a production build the overlay calls this instead of
   * showing the user an error they cannot act on: keep moving IN THE DIRECTION THEY WERE GOING (so
   * pressing Back into a broken step does not bounce them forward again), and end the tour if that
   * walks off either end. The tour degrades; it never sits there dimming the screen with nothing lit.
   */
  const skipUnavailable = useCallback(() => {
    if (!active) return
    const target = active.index + active.direction
    // Walking off either end ends the tour through endTour like every other exit, so a practice run
    // that finishes this way still disarms AND still tells the person nothing was saved. Returning
    // null here directly — which is what this did — ended a practice run in silence.
    if (target < 0 || target >= active.steps.length) { endTour(active, 'completed'); return }
    writeProgress(userId, active.tour.id, { stepId: active.steps[target].id, completedAt: null })
    setActive({ ...active, index: target, resumed: false })
  }, [active, userId, endTour])

  // Logging out mid-tour must not leave an overlay pinned over the login screen.
  useEffect(() => { if (!user) { setActive(null); setFinished(null) } }, [user])

  // ── Practice mode ─────────────────────────────────────────────────────────────────────────────
  /**
   * Armed for the WHOLE of a practice tour and disarmed by this effect's cleanup, which is what
   * makes "the interceptor cannot outlive the tour" a property of the mechanism rather than a
   * discipline. Every way a tour can end runs that cleanup: completing it, pressing X, Escape,
   * logging out (the effect above nulls `active`), navigating to a module that unmounts App, and
   * closing the tab. A reload does not need it — the patch lives in memory and the memory is gone,
   * and the tour does not silently resume itself, so there is never an armed run without its marker
   * on screen.
   *
   * Keyed on the tour ID, NOT on `active`: keying on the object would tear the patch down and put it
   * back on every single step, and each of those windows is a moment when a write could get out.
   */
  /**
   * THE QUERY CACHE AT BOTH ENDS OF EVERY RUN (review of 17f785bc, F2). A practice read's answer is
   * cached by whatever screen asked, and the cache outlives the screen: a run that ended while that
   * screen was unmounted left the practice record cached for the app's five minutes, to be shown to the
   * next run. This is the one place that holds the app's client and outlives every module, so it is
   * where the purges run — on every arm and every disarm, whether or not the run wrote anything. A
   * second pass after a disarm catches what the screen the run ended on was still holding.
   *
   * Read raw: a test tree with no query client is a tree with no cache to purge.
   */
  const queryClient = useContext(QueryClientContext)
  useEffect(() => {
    if (!queryClient) return undefined
    const timers = new Set()
    const off = subscribePracticeRuns(({ armed }) => {
      purgePracticeQueries(queryClient, { armed, late: false })
      if (armed) return
      const t = setTimeout(() => { timers.delete(t); purgePracticeQueries(queryClient, { armed: false, late: true }) }, 0)
      timers.add(t)
    })
    return () => { off(); for (const t of timers) clearTimeout(t) }
  }, [queryClient])

  const activeTourId = active ? active.tour.id : null
  const activeIsPractice = !!(active && active.tour.practice)
  useEffect(() => {
    if (!activeTourId || !activeIsPractice) return undefined
    return armPractice()
  }, [activeTourId, activeIsPractice])

  const step = active ? active.steps[active.index] : null

  // ── Route sync ────────────────────────────────────────────────────────────────────────────────
  // Activating a step whose screen we are not on navigates there ONCE. Once per step, tracked by
  // step id: without the guard, a user who deliberately walks away from the step's screen gets
  // yanked back on every render, which is the app fighting its user.
  const navigatedFor = useRef(null)
  useEffect(() => {
    if (!step) { navigatedFor.current = null; return }
    if (!step.route) return
    if (navigatedFor.current === step.id) return
    navigatedFor.current = step.id
    if (location.pathname !== step.route) navigate(step.route)
  }, [step, navigate, location.pathname])

  const value = useMemo(() => ({
    /** [{ tour, steps }] — already filtered to what this user can do. */
    entries,
    active,
    step,
    stepNumber: active ? active.index + 1 : 0,
    stepCount: active ? active.steps.length : 0,
    /** True while no mutating request can leave the browser. Drives every practice marker. */
    practice: activeIsPractice,
    /** The "nothing was saved" card, or null. */
    finished,
    dismissFinished: () => setFinished(null),
    stuckAfterMs,
    startTour,
    next,
    back,
    goTo,
    stop,
    skipUnavailable,
    progressFor: (tourId) => readProgress(userId, tourId),
  }), [
    entries, active, step, activeIsPractice, finished, stuckAfterMs,
    startTour, next, back, goTo, stop, skipUnavailable, userId,
  ])

  return (
    <TourContext.Provider value={value}>
      {children}
      {/* The OVERLAY mounts here because it must survive a cross-module navigation. The LAUNCHER
          does not: it is only an entry point, so it rides in AppHeader (shared/components/
          AppHeader.jsx) instead. A permanently-mounted floating button in the corner would sit on
          top of the app's own bottom-anchored action bars on every screen — see TourLauncher's
          header for the list. It renders nothing when there is no user or no eligible tour. */}
      <TourOverlay anchorTimeoutMs={anchorTimeoutMs} strict={strict} />
    </TourContext.Provider>
  )
}

export default TourProvider
